import { useMediaCollection } from '@/composables/useMediaCollection';
import { inCapacitor, promiseWithResolver } from '@/helpers';
import Permissions from '@/models/Permissions';
import User from '@/models/User';
import AuthService from '@/services/AuthService';
import CapacitorService, { StoreKeys } from '@/services/CapacitorService';
import UserService, { UserCreateRequest, UserUpdateRequest } from '@/services/UserService';
import Bugsnag from '@bugsnag/js';
import { defineStore } from 'pinia';
import { useRepo } from 'pinia-orm';
import { RouteRecordName } from 'vue-router';
import { useCompaniesStore } from './companies';
import { useNavStore } from './nav';
import { useProjectsStore } from './projects';
import { useRolesStore } from './roles';

export const useUserStore = defineStore('user', {
  state: () => ({
    user: undefined,
    userFetched: promiseWithResolver(),
    intendedRoute: undefined,
    userSignature: undefined,
  }),

  getters: {
    authenticated: (state) => state.user !== undefined,
    isAdmin: (state) => {
      if(!state.user) {
        return false;
      }

      const permissionsRepo = useRepo(Permissions);

      return permissionsRepo.where('user_id', (state.user as User).id)
        .where('role_id', 1).get().length > 0;
    },

    isClientManager(): () => boolean {
      return () => {
        return this.isRole(['client_manager'], false);
      };
    },

    currentProjectRole: (state) => {
      const projectsStore = useProjectsStore();
      const permissionsRepo = useRepo(Permissions);

      return permissionsRepo.where('user_id', (state.user as User)?.id)
        .where('project_id', projectsStore.project?.id)
        .with('role')
        .first()?.role?.descriptor;
    },

    isRole: (state) => (roles: RoleTypes[], includeAdmin = true, projectId: Id | undefined = undefined) => {
      const projectsStore = useProjectsStore();

      if(!state.user) {
        return false;
      }

      const rolesStore = useRolesStore();
      const rolesToCheck = rolesStore.models.whereIn('name', roles).get().map((role) => role.id);
      const permissionsRepo = useRepo(Permissions);
      const projectIdToCheck = projectId ? parseInt(projectId.toString()) : projectsStore.project?.id;

      return permissionsRepo
        .where('user_id', (state.user as User).id)
        .where((permission: Permissions) => {
          return (permission.project_id === projectIdToCheck &&
            rolesToCheck.includes(permission.role_id)) || (
              permission.role_id === 1 && includeAdmin
            );
        }).get().length > 0;
    },

    isRoleForCompany: (state) => (roles: RoleTypes[], includeAdmin = true, companyId: Id) => {
      if(!state.user) {
        return false;
      }

      const permissionsRepo = useRepo(Permissions);
      const rolesStore = useRolesStore();
      const rolesToCheck = rolesStore.models.whereIn('name', roles).get().map((role) => role.id);
      const companyIdToCheck = companyId ? parseInt(companyId.toString()) : undefined;

      return permissionsRepo
        .where('user_id', (state.user as User).id)
        .where((permission) => {
          return (permission.company_id === companyIdToCheck &&
            rolesToCheck.includes(permission.role_id)) || (
              permission.role_id === 1 && includeAdmin
            );
        })
        .get().length > 0;
    },

    models: () => useRepo(User),

    hasAccessToRoute(): (routeName: string | RouteRecordName, projectId: Id | undefined) => boolean {
      return (routeName: string | RouteRecordName, projectId: Id | undefined = undefined) => {
        const navStore = useNavStore();
        const route = navStore.routesAsMap.get(routeName);

        if(route) {
          if(route.meta.roles) {
            return this.isRole(route.meta.roles, true, projectId);
          } else if(route.meta.excludeRoles) {
            return !this.isRole(route.meta.excludeRoles, false, projectId);
          }

          return true;
        }

        return false;
      };
    },
  },

  actions: {
    async fetchLoggedInUser() {
      const [user, _error] = await AuthService.getLoggedInUser();

      this.userFetched.resolvePromise();

      if(user) {
        this.user = user;

        useMediaCollection(this.showSignature).fetchMedia().then((media) => {
          this.userSignature = media[0];
        });

        this.models.save(user);

        Bugsnag.addOnSession((session) => {
          session.setUser(user.id);
        });

        return user;
      }

      return undefined;
    },

    setUser(user: any) {
      this.user = user;

      this.models.save(user);

      Bugsnag.addOnSession((session) => {
        session.setUser(user.id);
      });
    },

    async signOut() {
      const [data, error] = await AuthService.logout();

      this.user = undefined;

      if(inCapacitor) {
        CapacitorService.delete(StoreKeys.AUTH_TOKEN);
      }

      if(error) {
        throw error;
      }

      return data;
    },

    async signIn(email: string, password: string) {
      const [data, error] = await AuthService.login({ email, password });

      if(error) {
        throw error;
      }

      const [user] = await AuthService.getLoggedInUser();

      this.setUser(user);

      return data;
    },

    async fetchUsers() {
      const [data, error] = await UserService.index();

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async fetchUser(id: Id) {
      const [data, error] = await UserService.show(id);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async updateUser(id: Id, form: UserUpdateRequest) {
      const [data, error] = await UserService.update(id, form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async updateMe(form: ApiRequests['api.v1.users.update-me']) {
      const [data, error] = await UserService.updateMe(form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async createUser(form: UserCreateRequest) {
      const [data, error] = await UserService.create(form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async deleteUser(id: Id) {
      const [data, error] = await UserService.delete(id);

      if(error) {
        throw error;
      }

      this.models.destroy(id);

      return data;
    },

    async disableUser(id: Id, form: ApiRequests['api.v1.users.toggle-disable']) {
      const [data, error] = await UserService.disable(id, form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async fetchAdminUsers(page = 1) {
      const [data, error] = await UserService.adminIndex(page);

      if(error) {
        throw error;
      }

      this.models.flush();
      this.models.save(data.users.data);

      return data;
    },

    async sendWelcomeEmail(id: Id) {
      const [data, error] = await UserService.admin.welcomeEmail(id);

      if(error) {
        throw error;
      }

      return data;
    },

    async fetchCompanyUsersIndex(id: Id) {
      const [data, error] = await UserService.company.index(id);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async attachCompanyUserRole(companyId: Id, projectId: Id, form: ApiRequests['api.v1.companies.user-roles.attach']) {
      const [data, error] = await UserService.company.attachRole(companyId, projectId, form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async detachCompanyUserRole(companyId: Id, projectId: Id, form: ApiRequests['api.v1.companies.user-roles.detach']) {
      const [data, error] = await UserService.company.detachRole(companyId, projectId, form);

      if(error) {
        throw error;
      }

      useRepo(Permissions).query().where('project_id', parseInt(projectId as string)).where('user_id', form.user_id)
        .delete();

      this.models.save(data);

      return data;
    },

    async createClient(projectId: Id, form: ApiRequests['api.v1.projects.clients.store']) {
      const [data, error] = await UserService.clients.create(projectId, form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async indexClient(projectId: Id) {
      const [data, error] = await UserService.clients.index(projectId);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async deleteClient(projectId: Id, clientId: Id) {
      const [data, error] = await UserService.clients.delete(projectId, clientId);

      if(error) {
        throw error;
      }

      useRepo(Permissions).query().where('project_id', parseInt(projectId as string)).where(
        'user_id',
        parseInt(clientId.toString()),
      ).delete();

      this.models.destroy(clientId);

      return data;
    },

    async searchClient(projectId: Id, form: ApiRequests['api.v1.projects.clients.search']) {
      const [data, error] = await UserService.clients.search(projectId, form);

      if(error) {
        throw error;
      }

      return data;
    },

    async inviteUser(projectId: Id, form: ApiRequests['api.v1.companies.users.store']) {
      const [data, error] = await UserService.company.invite(projectId, form);

      if(error) {
        throw error;
      }

      return data;
    },

    async searchUsers(companyId: Id, form: ApiRequests['api.v1.companies.users.search']) {
      const [data, error] = await UserService.company.search(companyId, form);

      if(error) {
        throw error;
      }

      return data;
    },

    async showSignature() {
      const [data, error] = await UserService.signature.show();

      if(error) {
        throw error;
      }

      return data;
    },

    async storeSignature(form: ApiRequests['api.v1.users.signature.store'] | FormData) {
      const [data, error] = await UserService.signature.store(form);

      if(error) {
        throw error;
      }

      return data;
    },
  },

  persist: {
    storage: localStorage,
    paths: ['user'],
  },
});
